import React from 'react';
import { Stack, Divider } from '@mui/material';
import { InvoiceCheckFrequency, POCheckFrequency } from '../../types';
import { ERPGatewayConfigurationFormViewModel } from '../../services/backendServices/ViewModels/ERPGatewayConfigurationViewModel';
import { ERPConnectionInfoEditor } from './ERPConnectionInfoEditor';
import { SortedSelect, TextField, TimePicker } from '../../ui';

const TIMEZONE_LABEL_STRING = ' (PT)';
interface ERPGatewayConfigurationEditorProps {
    formData: ERPGatewayConfigurationFormViewModel;
    handleFieldChange: (field: string, value: any) => void;
}

export const InvoiceCheckFrequencyOptions = [
    { label: 'None', value: null },
    { label: 'Every 10 minutes', value: InvoiceCheckFrequency.TenMinutes },
    { label: 'Every 30 minutes', value: InvoiceCheckFrequency.ThirtyMinutes },
    { label: 'Hourly', value: InvoiceCheckFrequency.Hourly },
    { label: 'Daily', value: InvoiceCheckFrequency.Daily },
];

export const POCheckFrequencyOptions = [
    { label: 'None', value: null },
    { label: 'Daily', value: POCheckFrequency.Daily },
    { label: 'Weekly', value: POCheckFrequency.Weekly },
    { label: 'Monthly', value: POCheckFrequency.Monthly },
];

export const ERPGatewayConfigurationEditorStrings = {
    ERPName: 'ERP Name',
    ERPVersion: 'ERP Version',
    InvoiceWorkingDirectory: 'Invoice Working Directory',
    POWorkingDirectory: 'PO Working Directory',
    InvoiceCheckFrequency: 'Invoice Check Frequency',
    InvoiceCheckTimeOfDay: `Invoice Check Time of Day ${TIMEZONE_LABEL_STRING}`,
    POCheckFrequency: 'PO Check Frequency',
    POCheckTimeOfDay: `PO Check Time of Day ${TIMEZONE_LABEL_STRING}`,
    ERPConnectionInfo: 'ERP Connection Info',
};

export const ERPGatewayConfigurationEditor: React.FC<ERPGatewayConfigurationEditorProps> = ({
    formData,
    handleFieldChange,
}) => {
    const handleERPConnectionInfoChange = (newConnectionInfo: Record<string, any>) => {
        const updatedConnectionInfo = {
            ...formData.ERPConnectionInfo,
            root: {
                ...newConnectionInfo,
            },
        };
        handleFieldChange('ERPConnectionInfo', updatedConnectionInfo);
    };

    return (
        <Stack direction="column" alignItems="flex-start" justifyContent="center" gap={2} py={1}>
            <TextField
                label={ERPGatewayConfigurationEditorStrings.ERPName}
                value={formData.ERPName}
                onChange={(e) => handleFieldChange('ERPName', e.target.value)}
                required
            />
            <TextField
                label={ERPGatewayConfigurationEditorStrings.ERPVersion}
                value={formData.ERPVersion}
                onChange={(e) => handleFieldChange('ERPVersion', e.target.value)}
                required
            />
            <TextField
                label={ERPGatewayConfigurationEditorStrings.InvoiceWorkingDirectory}
                value={formData.InvoiceWorkingDirectory}
                onChange={(e) => handleFieldChange('InvoiceWorkingDirectory', e.target.value)}
                required
            />
            <TextField
                label={ERPGatewayConfigurationEditorStrings.POWorkingDirectory}
                value={formData.POWorkingDirectory}
                onChange={(e) => handleFieldChange('POWorkingDirectory', e.target.value)}
                required
            />
            <SortedSelect
                label={ERPGatewayConfigurationEditorStrings.InvoiceCheckFrequency}
                value={
                    InvoiceCheckFrequencyOptions.find((option) => option.value === formData.InvoiceCheckFrequency) ??
                    InvoiceCheckFrequencyOptions[0]
                }
                onChange={(e) => {
                    handleFieldChange('InvoiceCheckFrequency', e?.value as InvoiceCheckFrequency);
                }}
                options={InvoiceCheckFrequencyOptions}
                disableSorting
                required
            />
            <TimePicker
                label={ERPGatewayConfigurationEditorStrings.InvoiceCheckTimeOfDay}
                value={formData.InvoiceCheckTimeOfDay}
                onChange={(value) => handleFieldChange('InvoiceCheckTimeOfDay', value)}
                required
            />
            <SortedSelect
                label={ERPGatewayConfigurationEditorStrings.POCheckFrequency}
                value={
                    POCheckFrequencyOptions.find((option) => option.value === formData.POCheckFrequency) ??
                    POCheckFrequencyOptions[0]
                }
                onChange={(e) => {
                    handleFieldChange('POCheckFrequency', e?.value as POCheckFrequency);
                }}
                options={POCheckFrequencyOptions}
                disableSorting
                required
            />
            <TimePicker
                label={ERPGatewayConfigurationEditorStrings.POCheckTimeOfDay}
                value={formData.POCheckTimeOfDay}
                onChange={(value) => handleFieldChange('POCheckTimeOfDay', value)}
                required
            />
            {formData.ERPConnectionInfo && Object.keys(formData.ERPConnectionInfo).length > 0 && (
                <>
                    <Divider sx={{ width: '100%', my: 1 }} />

                    <ERPConnectionInfoEditor
                        title={ERPGatewayConfigurationEditorStrings.ERPConnectionInfo}
                        connectionInfo={formData.ERPConnectionInfo.root}
                        onChange={handleERPConnectionInfoChange}
                    />
                </>
            )}
        </Stack>
    );
};
